/**
 * Application route definitions with lazy-loaded route groups.
 *
 * Eager:  Dashboard, Profile, ApplicationsList, Loans (critical path)
 * Lazy:   Everything else — split by domain for optimal chunk sizes
 */
import React, { Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import { Route, Navigate } from 'react-router-dom';
import { TenantRoute, TenantAdminRoute, ModuleRoute, SystemAdminRoute, IndexRedirect } from '@/components/auth/RouteGuards';
import { Loader2 } from 'lucide-react';

// ── Eager-loaded (critical path) ────────────────────────────────
import Dashboard from '@/pages/Dashboard';
import ProfilePage from '@/pages/ProfilePage';
import Loans from '@/pages/Loans';

// ── Lazy-loaded: Loan Management ────────────────────────────────
const LoanTypes  = lazy(() => import('@/pages/LoanTypes'));
const LoanProductsPage = lazy(() => import('@/pages/LoanProducts'));
const DocumentTypes     = lazy(() => import('@/pages/DocumentTypes'));
const NewLoanPage   = lazy(() => import('@/pages/loans/NewLoanPage'));
const LoanDetail    = lazy(() => import('@/pages/LoanDetail'));
const Disbursements = lazy(() => import('@/pages/Disbursements'));
const Repayments    = lazy(() => import('@/pages/Repayments'));

// ── Lazy-loaded: Security ───────────────────────────────────────
const SecurityTypes       = lazy(() => import('@/pages/security/SecurityTypes'));
const Securities          = lazy(() => import('@/pages/security/Securities'));
const SecurityDetail      = lazy(() => import('@/pages/security/SecurityDetail'));
const SecurityAssignments = lazy(() => import('@/pages/security/SecurityAssignments'));
const AssignmentDetail    = lazy(() => import('@/pages/security/AssignmentDetail'));
const NewAssignment       = lazy(() => import('@/pages/security/NewAssignment'));
const SecurityShortfalls  = lazy(() => import('@/pages/security/SecurityShortfalls'));
const SecurityReleases    = lazy(() => import('@/pages/security/SecurityReleases'));
const SecurityDeposits    = lazy(() => import('@/pages/security/SecurityDeposits'));
const SecurityPrices      = lazy(() => import('@/pages/security/SecurityPrices'));
const SecurityPriceHistory = lazy(() => import('@/pages/security/SecurityPriceHistory'));

// ── Lazy-loaded: Charges & Demands ──────────────────────────────
const InterestAccruals    = lazy(() => import('@/pages/charges/InterestAccruals'));
const LoanAdjustments     = lazy(() => import('@/pages/charges/LoanAdjustments'));
const LoanDemands         = lazy(() => import('@/pages/demands/LoanDemands'));
const OverdueLoans        = lazy(() => import('@/pages/demands/OverdueLoans'));
const WaiverProcessing    = lazy(() => import('@/pages/demands/WaiverProcessing'));
const PaymentApplication  = lazy(() => import('@/pages/demands/PaymentApplication'));

// ── Lazy-loaded: Partners & Recovery ────────────────────────────
const LoanPartners        = lazy(() => import('@/pages/partners/LoanPartners'));
const LoanTransfers       = lazy(() => import('@/pages/partners/LoanTransfers'));
const CoLendingManagement = lazy(() => import('@/pages/partners/CoLendingManagement'));
const LoanRestructures    = lazy(() => import('@/pages/recovery/LoanRestructures'));
const LoanFreeze          = lazy(() => import('@/pages/recovery/LoanFreeze'));
const LoanWriteOffs       = lazy(() => import('@/pages/recovery/LoanWriteOffs'));
const LoanRefunds         = lazy(() => import('@/pages/recovery/LoanRefunds'));

// ── Lazy-loaded: Settings & Tools ───────────────────────────────
const AccountingSettings = lazy(() => import('@/pages/settings/AccountingSettings'));
const ProcessJobs        = lazy(() => import('@/pages/settings/ProcessJobs'));
const DemandOffsetOrder  = lazy(() => import('@/pages/tools/DemandOffsetOrder'));
const BulkRepayment      = lazy(() => import('@/pages/tools/BulkRepayment'));
const RepostTools        = lazy(() => import('@/pages/tools/RepostTools'));
const AdminTools         = lazy(() => import('@/pages/tools/AdminTools'));
const RiskDashboard      = lazy(() => import('@/pages/risk/RiskDashboard'));
const ReportsHub         = lazy(() => import('@/pages/reports/ReportsHub'));

// ── Lazy-loaded: Compliance ──────────────────────────────────────
const CibilReportingPage = lazy(() => import('@/pages/compliance/CibilReportingPage'));
const CancellationsPage = lazy(() => import('@/pages/compliance/CancellationsPage'));

// ── Lazy-loaded: Number Cards ───────────────────────────────────

// ── Lazy-loaded: Settings ───────────────────────────────────────
const IntegrationsPage = lazy(() => import('@/pages/settings/IntegrationsPage'));

// ── Lazy-loaded: Platform Configuration ─────────────────────────
const ChargeConfigurationPage = lazy(() => import('@/pages/platform-config/charge-configuration/ChargeConfigurationPage'));
const InterestRateConfigurationPage = lazy(() => import('@/pages/platform-config/interest-rate-configuration/InterestRateConfigurationPage'));
const AgreementConfigurationPage = lazy(() => import('@/pages/platform-config/agreement-configuration/AgreementConfigurationPage'));
const EmailNotificationsPage = lazy(() => import('@/pages/platform-config/notifications/email/EmailNotificationsPage'));
const SmsNotificationsPage = lazy(() => import('@/pages/platform-config/notifications/sms/SmsNotificationsPage'));

// ── Lazy-loaded: Agreements ─────────────────────────────────────
const AgreementsListPage = lazy(() => import('@/pages/agreements/AgreementsListPage'));

// ── Lazy-loaded: User Management ────────────────────────────────
const UsersDirectory   = lazy(() => import('@/pages/user-management/UsersDirectory'));
const RoleManagement   = lazy(() => import('@/pages/user-management/RoleManagement'));
const BranchesPage     = lazy(() => import('@/pages/user-management/BranchesPage'));
const MFASettings      = lazy(() => import('@/pages/user-management/MFASettings'));

// ── Lazy-loaded: Tenant Admin ───────────────────────────────────
const TenantSettings     = lazy(() => import('@/pages/tenant/TenantSettings'));
const EmailLogs          = lazy(() => import('@/pages/tenant/EmailLogs'));
const CreditDashboard    = lazy(() => import('@/pages/tenant/CreditDashboard'));

// ── Lazy-loaded: System Admin ───────────────────────────────────
const SystemTenants    = lazy(() => import('@/pages/system/SystemTenants'));
const EmailConfig      = lazy(() => import('@/pages/system/EmailConfig'));
const EmailTemplates   = lazy(() => import('@/pages/system/EmailTemplates'));
const CreditConfig     = lazy(() => import('@/pages/system/CreditConfig'));
const CreditManagement = lazy(() => import('@/pages/system/CreditManagement'));
const StorageSettings  = lazy(() => import('@/pages/system/StorageSettings'));
const CaptchaSettings  = lazy(() => import('@/pages/system/CaptchaSettings'));
const PlatformDashboard = lazy(() => import('@/pages/system/PlatformDashboard'));


// ── Suspense wrapper ────────────────────────────────────────────
function L({ children }) {
  return (
    <Suspense fallback={
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    }>
      {children}
    </Suspense>
  );
}

L.propTypes = {
  children: PropTypes.node.isRequired,
};


export default function AppRoutes() {
  return (
    <>
      {/* Eager — critical path */}
      <Route path="/" element={<IndexRedirect />} />
      <Route path="/dashboard" element={<TenantRoute><Dashboard /></TenantRoute>} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/loans" element={<ModuleRoute module="loan_management"><Loans /></ModuleRoute>} />
      <Route path="/settings" element={<Navigate to="/profile" replace />} />

      {/* Loan Management — /loans/new before /loans/:id so "new" is not treated as an id */}
      <Route path="/loan-types" element={<ModuleRoute module="loan_management"><L><LoanTypes /></L></ModuleRoute>} />
      <Route path="/loan-products" element={<ModuleRoute module="loan_management"><L><LoanProductsPage /></L></ModuleRoute>} />
      <Route path="/document-types" element={<ModuleRoute module="loan_management"><L><DocumentTypes /></L></ModuleRoute>} />
      <Route path="/loans/new" element={<ModuleRoute module="loan_management"><L><NewLoanPage /></L></ModuleRoute>} />
      <Route path="/loans/:id" element={<ModuleRoute module="loan_management"><L><LoanDetail /></L></ModuleRoute>} />
      <Route path="/disbursements" element={<ModuleRoute module="loan_management"><L><Disbursements /></L></ModuleRoute>} />
      <Route path="/repayments" element={<ModuleRoute module="loan_management"><L><Repayments /></L></ModuleRoute>} />

      {/* Security */}
      <Route path="/security/types" element={<ModuleRoute module="security_collateral"><L><SecurityTypes /></L></ModuleRoute>} />
      <Route path="/security/securities" element={<ModuleRoute module="security_collateral"><L><Securities /></L></ModuleRoute>} />
      <Route path="/security/securities/:id" element={<ModuleRoute module="security_collateral"><L><SecurityDetail /></L></ModuleRoute>} />
      <Route path="/security/assignments" element={<ModuleRoute module="security_collateral"><L><SecurityAssignments /></L></ModuleRoute>} />
      <Route path="/security/assignments/new" element={<ModuleRoute module="security_collateral"><L><NewAssignment /></L></ModuleRoute>} />
      <Route path="/security/assignments/:id" element={<ModuleRoute module="security_collateral"><L><AssignmentDetail /></L></ModuleRoute>} />
      <Route path="/security/shortfalls" element={<ModuleRoute module="security_collateral"><L><SecurityShortfalls /></L></ModuleRoute>} />
      <Route path="/security/releases" element={<ModuleRoute module="security_collateral"><L><SecurityReleases /></L></ModuleRoute>} />
      <Route path="/security/deposits" element={<ModuleRoute module="security_collateral"><L><SecurityDeposits /></L></ModuleRoute>} />
      <Route path="/security/prices" element={<ModuleRoute module="security_collateral"><L><SecurityPrices /></L></ModuleRoute>} />
      <Route path="/security/prices/history" element={<ModuleRoute module="security_collateral"><L><SecurityPriceHistory /></L></ModuleRoute>} />

      {/* Charges & Demands — Charge Types moved to Platform Configuration */}
      <Route path="/charges/accruals" element={<ModuleRoute module="charges_interest"><L><InterestAccruals /></L></ModuleRoute>} />
      <Route path="/charges/adjustments" element={<ModuleRoute module="charges_interest"><L><LoanAdjustments /></L></ModuleRoute>} />
      <Route path="/demands" element={<ModuleRoute module="demands_collections"><L><LoanDemands /></L></ModuleRoute>} />
      <Route path="/demands/overdue" element={<ModuleRoute module="demands_collections"><L><OverdueLoans /></L></ModuleRoute>} />
      <Route path="/demands/waivers" element={<ModuleRoute module="demands_collections"><L><WaiverProcessing /></L></ModuleRoute>} />
      <Route path="/demands/payments" element={<ModuleRoute module="demands_collections"><L><PaymentApplication /></L></ModuleRoute>} />

      {/* Partners & Recovery */}
      <Route path="/partners" element={<ModuleRoute module="co_lending_transfers"><L><LoanPartners /></L></ModuleRoute>} />
      <Route path="/transfers" element={<ModuleRoute module="co_lending_transfers"><L><LoanTransfers /></L></ModuleRoute>} />
      <Route path="/co-lending" element={<ModuleRoute module="co_lending_transfers"><L><CoLendingManagement /></L></ModuleRoute>} />
      <Route path="/restructures" element={<ModuleRoute module="loan_operations"><L><LoanRestructures /></L></ModuleRoute>} />
      <Route path="/risk/restructure-limit-log" element={<Navigate to="/restructures" replace />} />
      <Route path="/risk/restructure-log" element={<Navigate to="/restructures" replace />} />
      <Route path="/loan-freeze" element={<ModuleRoute module="loan_operations" allowedRoles={['risk_manager']}><L><LoanFreeze /></L></ModuleRoute>} />
      <Route path="/write-offs" element={<ModuleRoute module="loan_operations"><L><LoanWriteOffs /></L></ModuleRoute>} />
      <Route path="/refunds" element={<ModuleRoute module="loan_operations"><L><LoanRefunds /></L></ModuleRoute>} />

      {/* Settings & Tools */}
      <Route path="/accounting" element={<ModuleRoute module="system"><L><AccountingSettings /></L></ModuleRoute>} />
      <Route path="/process-jobs" element={<ModuleRoute module="system"><L><ProcessJobs /></L></ModuleRoute>} />
      <Route path="/tools/offset-order" element={<ModuleRoute module="tools"><L><DemandOffsetOrder /></L></ModuleRoute>} />
      <Route path="/tools/bulk-repayment" element={<ModuleRoute module="tools"><L><BulkRepayment /></L></ModuleRoute>} />
      <Route path="/tools/repost" element={<ModuleRoute module="tools"><L><RepostTools /></L></ModuleRoute>} />
      <Route path="/risk" element={<ModuleRoute module="system"><L><RiskDashboard /></L></ModuleRoute>} />
      <Route path="/reports" element={<ModuleRoute module="reports"><L><ReportsHub /></L></ModuleRoute>} />
      <Route path="/admin-tools" element={<ModuleRoute module="tools"><L><AdminTools /></L></ModuleRoute>} />

      {/* Compliance */}
      <Route path="/compliance/cibil-reporting" element={<ModuleRoute module="compliance"><L><CibilReportingPage /></L></ModuleRoute>} />
      <Route path="/compliance/cancellations" element={<ModuleRoute module="compliance"><L><CancellationsPage /></L></ModuleRoute>} />

      {/* Agreements */}
      <Route path="/agreements" element={<ModuleRoute module="loan_management"><L><AgreementsListPage /></L></ModuleRoute>} />

      {/* Settings */}
      <Route path="/settings/integrations" element={<ModuleRoute module="settings"><L><IntegrationsPage /></L></ModuleRoute>} />

      {/* Platform Configuration */}
      <Route path="/platform-config" element={<TenantAdminRoute><Navigate to="/platform-config/charges" replace /></TenantAdminRoute>} />
      <Route path="/platform-config/charges" element={<TenantRoute><L><ChargeConfigurationPage /></L></TenantRoute>} />
      <Route path="/platform-config/interest-rates" element={<TenantAdminRoute><L><InterestRateConfigurationPage /></L></TenantAdminRoute>} />
      <Route path="/platform-config/agreements" element={<TenantAdminRoute><L><AgreementConfigurationPage /></L></TenantAdminRoute>} />
      <Route path="/platform-config/notifications" element={<TenantAdminRoute><Navigate to="/platform-config/notifications/email" replace /></TenantAdminRoute>} />
      <Route path="/platform-config/notifications/email" element={<TenantAdminRoute><L><EmailNotificationsPage /></L></TenantAdminRoute>} />
      <Route path="/platform-config/notifications/sms" element={<TenantAdminRoute><L><SmsNotificationsPage /></L></TenantAdminRoute>} />

      {/* User Management — Users/Roles/Branches via module; MFA stays tenant-admin */}
      <Route path="/user-management/users" element={<ModuleRoute module="user_management"><L><UsersDirectory /></L></ModuleRoute>} />
      <Route path="/user-management/roles" element={<ModuleRoute module="user_management"><L><RoleManagement /></L></ModuleRoute>} />
      <Route path="/user-management/branches" element={<ModuleRoute module="user_management"><L><BranchesPage /></L></ModuleRoute>} />
      <Route path="/user-management/mfa" element={<TenantAdminRoute><L><MFASettings /></L></TenantAdminRoute>} />
      <Route path="/tenant/users" element={<Navigate to="/user-management/users" replace />} />

      {/* Tenant Admin / Company Admin */}
      <Route path="/tenant/settings" element={<TenantAdminRoute><L><TenantSettings /></L></TenantAdminRoute>} />
      <Route path="/tenant/credits" element={<ModuleRoute module="company_admin"><L><CreditDashboard /></L></ModuleRoute>} />

      {/* System Admin */}
      <Route path="/system/dashboard" element={<SystemAdminRoute><L><PlatformDashboard /></L></SystemAdminRoute>} />
      <Route path="/system/tenants" element={<SystemAdminRoute><L><SystemTenants /></L></SystemAdminRoute>} />
      <Route path="/system/email-config" element={<SystemAdminRoute><L><EmailConfig /></L></SystemAdminRoute>} />
      <Route path="/system/email-templates" element={<SystemAdminRoute><L><EmailTemplates /></L></SystemAdminRoute>} />
      <Route path="/system/email-logs" element={<SystemAdminRoute><L><EmailLogs /></L></SystemAdminRoute>} />
      <Route path="/system/credit-config" element={<SystemAdminRoute><L><CreditConfig /></L></SystemAdminRoute>} />
      <Route path="/system/credit-management" element={<SystemAdminRoute><L><CreditManagement /></L></SystemAdminRoute>} />
      <Route path="/system/storage" element={<SystemAdminRoute><L><StorageSettings /></L></SystemAdminRoute>} />
      <Route path="/system/captcha" element={<SystemAdminRoute><L><CaptchaSettings /></L></SystemAdminRoute>} />
      <Route path="/system/agreement-templates" element={<SystemAdminRoute><L><AgreementConfigurationPage /></L></SystemAdminRoute>} />
    </>
  );
}
